import * as Logger from './logger.js';
import { HSComponent } from './component.js';
import { escapeHtmlAttribute } from './utils.js';
import './toolbar-button.js';
import styles from './ml-labeling.css';
import pipelineStyles from './pipeline-sandbox.css';
import sharedPanelStyles from './ml-panel-shared.css';
import template from './ml-labeling.html';

// Labeling queue — the last of the three planned ML surfaces
// (docs/ML_FRONTEND_BUILD_PLAN.md §2). Engineer-facing review list, two
// buttons (yes/no), renders strictly from each request's `context` snapshot —
// never re-queries live data, since the reviewer must judge the model against
// exactly what it saw, not what's true now.
//
// Response shape for `GET /labels/requests` is frozen (spec §4); `GET
// /labels/datasets` is NOT (no example given anywhere) — rendered generically,
// best-effort. See the known gap note on request identity below.

export class HSMlLabeling extends HSComponent {
    #baseUrl = '/elm/api/ml';
    #requests = [];
    #requestsLoaded = false;
    #requestsError = null;
    #showReviewed = false;
    #selectedIndex = null;
    #actionStatus = null;
    #datasets = [];
    #datasetsLoaded = false;
    #datasetsError = null;

    get baseUrl() { return this.#baseUrl; }
    set baseUrl(v) { this.#baseUrl = v || '/elm/api/ml'; }

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(pipelineStyles);
        this.loadStyles(sharedPanelStyles);
        this.loadStyles(styles);
        this.#bindEvents();
        this.#fetchRequests();
        this.#fetchDatasets();
    }

    #bindEvents() {
        const sd = this.shadowRoot;
        this.onEvent('click', () => this.#close(), sd.getElementById('mll-exit-btn'));
        this.onEvent('click', () => this.#fetchRequests(), sd.getElementById('mll-refresh-btn'));
        this.onEvent('change', (_, ev) => {
            this.#showReviewed = ev.target.checked;
            this.#renderList();
        }, sd.getElementById('mll-show-reviewed'));
    }

    #close() {
        this.emit({ type: 'hs-close-ml-labeling' });
    }

    #esc(str) {
        return escapeHtmlAttribute(str);
    }

    #formatTs(ms) {
        return typeof ms === 'number' ? new Date(ms).toLocaleString() : '—';
    }

    // request identity — a known gap. The frozen example in spec §4 has no `id`
    // field on a label request even though `POST /labels/requests/:id` requires
    // one; try the plausible field names and disable submission if none exist
    // rather than fabricating one.
    #requestId(r) {
        return r.id ?? r.request_id ?? null;
    }

    // ---- Requests ----

    async #fetchRequests() {
        this.#requestsLoaded = false;
        this.#renderList();
        try {
            const res = await this.fetch(`${this.#baseUrl}/labels/requests`);
            this.#requests = res?.requests || [];
            this.#requestsError = null;
        } catch (err) {
            Logger.error('Failed to fetch label requests: ', err);
            this.#requests = [];
            this.#requestsError = err;
        }
        this.#requestsLoaded = true;
        this.#selectedIndex = null;
        this.#renderList();
        this.#renderDetail();
    }

    #visibleRequests() {
        return this.#showReviewed ? this.#requests : this.#requests.filter(r => r.label === null || r.label === undefined);
    }

    #renderList() {
        const el = this.shadowRoot.getElementById('mll-list');
        if (!el) return;
        if (!this.#requestsLoaded) {
            el.innerHTML = '<div class="mlw-placeholder">Loading label requests…</div>';
            return;
        }
        if (this.#requestsError) {
            el.innerHTML = `<div class="mlw-error-row">Failed to load: ${this.#esc(this.#requestsError.message || String(this.#requestsError))}</div>`;
            return;
        }
        const visible = this.#visibleRequests();
        if (!visible.length) {
            el.innerHTML = `<div class="mlw-placeholder">${this.#showReviewed ? 'No label requests.' : 'No pending label requests — nice.'}</div>`;
            return;
        }
        el.innerHTML = visible.map((r, i) => {
            const idx = this.#requests.indexOf(r);
            const labelBadge = r.label === null || r.label === undefined ? 'pending' : r.label ? 'yes' : 'no';
            const confidence = r.confidence === null || r.confidence === undefined ? 'not yet calibrated' : r.confidence;
            return `<div class="mll-row${this.#selectedIndex === idx ? ' mll-row-selected' : ''}" data-index="${idx}">
                <div class="mll-row-entity">${this.#esc(r.entity_key)}</div>
                <div class="mll-row-model">${this.#esc(r.model_ref)}@${this.#esc(r.model_version)}</div>
                <div class="mll-row-meta">
                    <span>prediction: ${r.prediction ? 'yes' : 'no'} · confidence: ${this.#esc(confidence)}</span>
                    <span class="mll-label-badge ${labelBadge}">${labelBadge}</span>
                </div>
            </div>`;
        }).join('');
        for (const row of el.querySelectorAll('.mll-row')) {
            this.onEvent('click', () => { this.#selectedIndex = Number(row.dataset.index); this.#actionStatus = null; this.#renderList(); this.#renderDetail(); }, row);
        }
    }

    #renderDetail() {
        const el = this.shadowRoot.getElementById('mll-detail');
        if (!el) return;
        if (this.#selectedIndex === null || !this.#requests[this.#selectedIndex]) {
            el.innerHTML = '<div class="mlw-placeholder">Select a request to review its context snapshot.</div>';
            return;
        }
        const r = this.#requests[this.#selectedIndex];
        const requestId = this.#requestId(r);
        const confidence = r.confidence === null || r.confidence === undefined ? 'not yet calibrated' : r.confidence;
        const labelText = r.label === null || r.label === undefined ? 'awaiting review' : r.label ? 'yes' : 'no';

        el.innerHTML = `
            <div class="mll-detail-header">${this.#esc(r.entity_key)}</div>
            <div class="mll-kv-row"><span>Model</span><span>${this.#esc(r.model_ref)}@${this.#esc(r.model_version)}</span></div>
            <div class="mll-kv-row"><span>Window</span><span>${this.#formatTs(r.window_start_ts)} \u2192 ${this.#formatTs(r.window_end_ts)}</span></div>
            <div class="mll-kv-row"><span>Prediction</span><span>${r.prediction ? 'yes' : 'no'}</span></div>
            <div class="mll-kv-row"><span>Confidence</span><span>${this.#esc(confidence)}</span></div>
            <div class="mll-kv-row"><span>Reason</span><span>${this.#esc(r.reason)}</span></div>
            <div class="mll-kv-row"><span>Requested</span><span>${this.#formatTs(r.requested_ts)}</span></div>
            <div class="mll-kv-row"><span>Current label</span><span>${this.#esc(labelText)}</span></div>
            <div class="mll-context">
                <div class="mlw-param-label">Context snapshot (what the model saw — never re-queried)</div>
                <pre>${this.#esc(JSON.stringify(r.context, null, 2))}</pre>
            </div>
            <div class="mll-actions-row">
                <button id="mll-label-yes" class="btn btn-sm btn-primary"${requestId === null ? ' disabled' : ''} title="${requestId === null ? 'No request id in this record — cannot submit a label yet' : ''}">Yes, correct</button>
                <button id="mll-label-no" class="btn btn-sm"${requestId === null ? ' disabled' : ''} title="${requestId === null ? 'No request id in this record — cannot submit a label yet' : ''}">No, incorrect</button>
            </div>
            <div id="mll-action-status" class="mlw-preview-status"></div>
        `;

        this.onEvent('click', () => this.#submitLabel(true), this.shadowRoot.getElementById('mll-label-yes'));
        this.onEvent('click', () => this.#submitLabel(false), this.shadowRoot.getElementById('mll-label-no'));
        this.#renderActionStatus();
    }

    #renderActionStatus() {
        const el = this.shadowRoot.getElementById('mll-action-status');
        if (!el) return;
        if (!this.#actionStatus) { el.textContent = ''; el.className = 'mlw-preview-status'; return; }
        el.textContent = this.#actionStatus.message;
        el.className = `mlw-preview-status${this.#actionStatus.ok ? '' : ' mlw-error-row'}`;
    }

    async #submitLabel(label) {
        const r = this.#requests[this.#selectedIndex];
        const requestId = r && this.#requestId(r);
        if (requestId === null || requestId === undefined) return;
        try {
            await this.fetch(`${this.#baseUrl}/labels/requests/${encodeURIComponent(requestId)}`, {
                method: 'POST',
                payload: { label },
            });
            r.label = label;
            this.#actionStatus = { ok: true, message: 'Label submitted.' };
            this.#renderList();
            this.#renderDetail();
        } catch (err) {
            Logger.error('Label submit failed: ', err);
            this.#actionStatus = { ok: false, message: `Label submit failed: ${err.message || err}` };
            this.#renderActionStatus();
        }
    }

    // ---- Datasets (best-effort — GET /labels/datasets has no frozen shape) ----

    async #fetchDatasets() {
        this.#datasetsLoaded = false;
        try {
            const res = await this.fetch(`${this.#baseUrl}/labels/datasets`);
            this.#datasets = res?.datasets || [];
            this.#datasetsError = null;
        } catch (err) {
            Logger.debug('Labeled datasets fetch failed (shape unconfirmed, may not exist yet): ', err);
            this.#datasets = [];
            this.#datasetsError = err;
        }
        this.#datasetsLoaded = true;
        this.#renderDatasets();
    }

    #renderDatasets() {
        const el = this.shadowRoot.getElementById('mll-datasets');
        if (!el) return;
        if (!this.#datasetsLoaded) { el.innerHTML = ''; return; }
        if (this.#datasetsError) { el.innerHTML = ''; return; }
        if (!this.#datasets.length) {
            el.innerHTML = '<div class="mlw-param-label">Labeled datasets</div><div class="mlw-placeholder">No labeled datasets yet.</div>';
            return;
        }
        const rows = this.#datasets.map(d => `<div class="mll-dataset-row">
            <span>${this.#esc(d.name)}${d.version !== undefined ? ` (v${this.#esc(d.version)})` : ''}</span>
            <button class="btn btn-sm" data-snapshot="${this.#esc(d.name)}">Snapshot</button>
        </div>`).join('');
        el.innerHTML = `<div class="mlw-param-label">Labeled datasets</div>${rows}`;
        for (const btn of el.querySelectorAll('[data-snapshot]')) {
            this.onEvent('click', () => this.#createSnapshot(btn.dataset.snapshot), btn);
        }
    }

    async #createSnapshot(name) {
        try {
            const res = await this.fetch(`${this.#baseUrl}/labels/datasets/${encodeURIComponent(name)}/snapshot`, { method: 'POST' });
            this.#actionStatus = { ok: true, message: `Snapshot created: version ${res?.version ?? '?'}.` };
        } catch (err) {
            Logger.error('Snapshot create failed: ', err);
            this.#actionStatus = { ok: false, message: `Snapshot failed: ${err.message || err}` };
        }
        this.#renderActionStatus();
        this.#fetchDatasets();
    }
}

customElements.define('hs-ml-labeling', HSMlLabeling);
